import React, { useEffect, useState } from "react";
import { Row, Col, Tag, Spin } from "antd";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import Breadcrumb from "../../components/Breadcrumbs/Breadcrumb";
import OrderStatusCard from "../../components/OrderCard";
import MultipleRoutesMap from "../../components/Map/ProjectMap";

import { fetchDashboard, fetchUserSurveys } from "../../services/authServices";
import { fetchProjectDropdownList } from "../../services/projectServices";
import { hasPermission } from "../../utils/permission";

interface Project {
  id: number;
  name?: string;
}

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const roleName = localStorage.getItem("role_name");
  const is_admin = roleName === "admin" ? 1 : 0;
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState<any>(null);
  const [surveyList, setSurveyList] = useState<any[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [selectedProjectId, setSelectedProjectId] = useState<number | null>(
    null
  );
  const [selectedProjectData, setSelectedProjectData] = useState<any>(null);
  const [selectedProjectSurveys, setSelectedProjectSurveys] = useState<any[]>(
    []
  );
  type DateFilterType = "last_week" | "last_month" | "custom";
  const [dateFilterType, setDateFilterType] = useState<DateFilterType>("last_week");
  const [customStartDate, setCustomStartDate] = useState("");
  const [customEndDate, setCustomEndDate] = useState("");

  // Fetch dashboard data on mount
  useEffect(() => {
    const loadDashboard = async () => {
      try {
        const res = await fetchDashboard(is_admin);
        setDashboardData(res.data);
        setSurveyList(res.data.surveys || []);
      } catch (error) {
        console.error("Dashboard fetch error:", error);
      } finally {
        setLoading(false);
      }
    };
    loadDashboard();
  }, []);

  // Fetch project list and auto-select first
  useEffect(() => {
    const loadProjects = async () => {
      try {
        const result = await fetchProjectDropdownList();
        setProjects(result);

        if (result.length > 0) {
          const firstProjectId = result[0].id;
          setSelectedProjectId(firstProjectId);

          const response = await fetchUserSurveys(
            firstProjectId,
            is_admin,
            dateFilterType,
            customStartDate || undefined,
            customEndDate || undefined
          );
          setSelectedProjectData(response?.data?.project);
          setSelectedProjectSurveys(response?.data?.surveys || []);
        }
      } catch (err: any) {
        toast.error(err.message || "Failed to fetch projects");
      }
    };
    loadProjects();
  }, []);
  useEffect(() => {
    if (dateFilterType === "custom") {
      const today = new Date().toISOString().split("T")[0];
      setCustomStartDate(today);
      setCustomEndDate(today);
    }
  }, [dateFilterType]);

  const handleStartDateChange = async (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const newStartDate = e.target.value;
    setCustomStartDate(newStartDate);

    // Ensure end date is not before start date
    if (customEndDate && customEndDate < newStartDate) {
      setCustomEndDate(newStartDate);
    }

    // Refetch data immediately
    if (selectedProjectId) {
      try {
        const response = await fetchUserSurveys(
          selectedProjectId,
          is_admin,
          "custom",
          newStartDate,
          customEndDate < newStartDate ? newStartDate : customEndDate
        );
        setSelectedProjectData(response?.data?.project);
        setSelectedProjectSurveys(response?.data?.surveys || []);
      } catch (err: any) {
        toast.error(
          err.message || "Failed to fetch surveys for new date range"
        );
      }
    }
  };
  const handleEndDateChange = async (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const newEndDate = e.target.value;
    setCustomEndDate(newEndDate);

    if (selectedProjectId) {
      try {
        const response = await fetchUserSurveys(
          selectedProjectId,
          is_admin,
          "custom",
          customStartDate,
          newEndDate
        );
        setSelectedProjectData(response?.data?.project);
        setSelectedProjectSurveys(response?.data?.surveys || []);
      } catch (err: any) {
        toast.error(
          err.message || "Failed to fetch surveys for new date range"
        );
      }
    }
  };

  const handleProjectChange = async (
    e: React.ChangeEvent<HTMLSelectElement>
  ) => {
    const projectId = Number(e.target.value);
    setSelectedProjectId(projectId);
    try {
      const response = await fetchUserSurveys(projectId, is_admin); // ✅ Pass both args
      setSelectedProjectData(response?.data?.project);
      setSelectedProjectSurveys(response?.data?.surveys || []);
    } catch (err: any) {
      toast.error(
        err.message || "Failed to fetch surveys for selected project"
      );
    }
  };

  const orderCardsData =
    dashboardData &&
    [
      hasPermission("project-view") && {
        title: "Projects",
        borderColor: "border-l-cyan-400",
        numberColor: "text-cyan-500",
        breakdown: {
          total: dashboardData.total.total_projects,
          running: dashboardData.total.running_projects,
          completed: dashboardData.total.completed_projects,
        },
        onClick: () => navigate("/dashboard/projectList"),
      },
      hasPermission("survey-view") && {
        title: "Surveys",
        borderColor: "border-l-purple-400",
        numberColor: "text-purple-500",
        breakdown: {
          total: dashboardData.total.total_surveys,
          running: dashboardData.total.running_surveys,
          completed: dashboardData.total.completed_surveys,
        },
        onClick: () => navigate("/dashboard/surveyListing"),
      },
      hasPermission("user-view") && {
        title: "Users",
        borderColor: "border-l-blue-400",
        numberColor: "text-blue-500",
        breakdown: {
          supervisors: dashboardData.total.total_supervisors,
          staff: dashboardData.total.total_staff,
          thirdParty: dashboardData.total.total_third_party,
        },
        onClick: () => navigate("/dashboard/userListing"),
      },
    ].filter(Boolean);
  const handleDateFilterChange = async (newFilter: DateFilterType) => {
    setDateFilterType(newFilter);

    try {
      if (selectedProjectId) {
        const response = await fetchUserSurveys(
          selectedProjectId,
          is_admin,
          newFilter,
          newFilter === "custom" ? customStartDate || undefined : undefined,
          newFilter === "custom" ? customEndDate || undefined : undefined
        );
        setSelectedProjectData(response?.data?.project);
        setSelectedProjectSurveys(response?.data?.surveys || []);
      }
    } catch (err: any) {
      console.log(err);
    }
  };

  return (
    <>
      <Breadcrumb pageName="Dashboard" />
      {loading ? (
        <div className="flex justify-center items-center h-96">
          <Spin size="large" />
        </div>
      ) : (
        <div className="space-y-6">
          {/* ==== Cards Section ==== */}
          <Row gutter={[24, 24]}>
            {orderCardsData && orderCardsData?.map((card: any, index: number) => (
              <Col xs={24} sm={12} lg={8} key={index}>
                <OrderStatusCard {...card} />
              </Col>
            ))}
          </Row>

          {/* ==== Latest Survey Table ==== */}
          {hasPermission("survey-view") &&
            surveyList &&
            surveyList.length > 0 && (
              <div className="rounded-md border bg-white p-5 shadow-sm dark:bg-boxdark dark:border-strokedark">
                <div className="flex justify-between items-center mb-4">
                  <h1 className="text-xl font-bold text-black">
                    📋 Latest 5 Survey
                  </h1>
                  <button
                    onClick={() => navigate("/dashboard/surveyListing")}
                    className="text-sm font-medium text-blue-600 hover:underline"
                  >
                    View All{" "}
                    <span className="text-sm font-normal text-blue-600">
                      ({dashboardData?.total?.total_surveys || 0})
                    </span>
                  </button>
                </div>

                <div className="overflow-x-auto">
                  <table className="w-full table-fixed">
                    <thead>
                      <tr className="bg-gray-2 dark:bg-meta-4 text-left">
                        <th className="py-4 px-4">Survey</th>
                        <th className="py-4 px-4">Project Name</th>
                        <th className="py-4 px-4">Start Date</th>
                        <th className="py-4 px-4">End Date</th>
                        <th className="py-4 px-4">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {surveyList && surveyList?.map((survey) => (
                        <tr key={survey.id}>
                          <td className="border-b py-4 px-4">{survey.name}</td>
                          <td className="border-b py-4 px-4">
                            {survey.project?.name}
                          </td>
                          <td className="border-b py-4 px-4">
                            {survey.start
                              ? new Date(survey.start).toLocaleDateString()
                              : "--"}
                          </td>
                          <td className="border-b py-4 px-4">
                            {survey.end
                              ? new Date(survey.end).toLocaleDateString()
                              : "--"}
                          </td>
                          <td className="border-b py-4 px-4">
                            <Tag
                              color={
                                survey.status?.toLowerCase() === "active"
                                  ? "green"
                                  : survey.status?.toLowerCase() === "inactive"
                                  ? "red"
                                  : "default"
                              }
                              className="uppercase font-medium"
                            >
                              {survey.status?.toUpperCase() || "--"}
                            </Tag>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

          {/* ==== Project Map Section ==== */}
          {hasPermission("survey-view") && projects && projects.length > 0 && (
            <div className="pt-10">
              <div className="flex justify-between items-center mb-4">
                <h1 className="text-xl font-bold text-black">Project Map</h1>
              </div>

              <div className="flex gap-4 flex-wrap mb-4">
                <div className="w-64">
                  <label className="block mb-2 text-sm font-medium text-gray-700 dark:text-white">
                    Select Project <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <select
                      name="project_id"
                      value={selectedProjectId ?? ""}
                      onChange={handleProjectChange}
                      className={`appearance-none w-full rounded-lg border py-2 px-4 dark:bg-form-input dark:text-white ${
                        !selectedProjectId
                          ? "border-red-500"
                          : "border-stroke dark:border-form-strokedark"
                      }`}
                    >
                      {projects && projects?.map((project) => (
                        <option key={project.id} value={project.id}>
                          {project.name}
                        </option>
                      ))}
                    </select>
                    <div className="absolute right-3 top-2/4 transform -translate-y-1/2 text-gray-400 pointer-events-none">
                      <svg
                        className="w-4 h-4"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M5.23 7.21a.75.75 0 011.06.02L10 10.94l3.71-3.71a.75.75 0 111.06 1.06l-4.24 4.25a.75.75 0 01-1.06 0L5.21 8.29a.75.75 0 01.02-1.06z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </div>
                  </div>
                </div>
                {/* ==== Date Range Dropdown ==== */}
                <div className="w-64">
                  <label className="block mb-2 text-sm font-medium text-gray-700 dark:text-white">
                    Project Duration
                  </label>
                  <div className="relative">
                    <select
                      name="date_range"
                      value={dateFilterType}
                      onChange={(e) =>
                        handleDateFilterChange(e.target.value as DateFilterType)
                      }
                      className="appearance-none w-full rounded-lg border py-2 px-4 dark:bg-form-input dark:text-white border-stroke dark:border-form-strokedark"
                    >
                      <option value="last_week">Last Week</option>
                      <option value="last_month">Last Month</option>
                      <option value="custom">Custom</option>
                    </select>
                    <div className="absolute right-3 top-2/4 transform -translate-y-1/2 text-gray-400 pointer-events-none">
                      <svg
                        className="w-4 h-4"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M5.23 7.21a.75.75 0 011.06.02L10 10.94l3.71-3.71a.75.75 0 111.06 1.06l-4.24 4.25a.75.75 0 01-1.06 0L5.21 8.29a.75.75 0 01.02-1.06z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </div>
                  </div>
                </div>
                {/* ==== Date Picker for Custom Option ==== */}
                {dateFilterType === "custom" && (
                  <div className="w-auto">
                    <label className="block mb-2 text-sm font-medium text-gray-700 dark:text-white">
                      Select Start / End Date
                    </label>
                    <input
                      type="date"
                      value={customStartDate}
                      onChange={handleStartDateChange}
                      className="border rounded-lg py-2 px-4 mr-2 dark:bg-form-input dark:text-white border-stroke dark:border-form-strokedark"
                    />
                    <input
                      type="date"
                      value={customEndDate}
                      min={customStartDate} // ✅ prevents earlier dates than start date
                      onChange={handleEndDateChange}
                      className="border rounded-lg py-2 px-4 dark:bg-form-input dark:text-white border-stroke dark:border-form-strokedark"
                    />
                  </div>
                )}
              </div>

              <div className="flex justify-center">
                {selectedProjectData ? (
                  <MultipleRoutesMap
                    startLat={parseFloat(selectedProjectData.start_latitude)}
                    startLng={parseFloat(selectedProjectData.start_longitude)}
                    endLat={parseFloat(selectedProjectData.end_latitude)}
                    endLng={parseFloat(selectedProjectData.end_longitude)}
                    startLocation={selectedProjectData.start_location}
                    endLocation={selectedProjectData.end_location}
                    submissions={selectedProjectSurveys}
                  />
                ) : (
                  <MultipleRoutesMap
                    startLat={28.6139}
                    startLng={77.209}
                    endLat={28.7041}
                    endLng={77.1025}
                    startLocation="Default Start Location"
                    endLocation="Default End Location"
                    submissions={[]}
                  />
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </>
  );
};

export default Dashboard;
